import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtNum, timeAgo } from "@/lib/format";

function Card({ title, children, testid, className = "" }) {
  return (
    <div className={`border border-zinc-800 rounded-md p-4 bg-zinc-950/35 ${className}`} data-testid={testid}>
      <div className="font-heading font-semibold text-zinc-100 mb-2">{title}</div>
      {children}
    </div>
  );
}

function StatusPill({ tone = "zinc", children }) {
  const tones = {
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    zinc: "border-zinc-700 bg-zinc-900 text-zinc-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] ${tones[tone]}`}>{children}</span>;
}

function Metric({ label, value, tone }) {
  return (
    <div className="border border-zinc-800 rounded-md p-3 bg-[#09090b]">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-lg font-mono-num mt-1 ${tone || "text-zinc-100"}`}>{fmtNum(value)}</div>
    </div>
  );
}


function trustTone(status) {
  if (status === "pass") return "emerald";
  if (status === "warn") return "amber";
  if (status === "fail") return "rose";
  return "zinc";
}

function TrustHealthCard({ trust }) {
  const checks = trust?.checks || [];
  return (
    <Card title="Production trust health" testid="trust-health" className="border-zinc-700">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <StatusPill tone={trustTone(trust?.overall_status)}>{trust?.overall_status || "loading"}</StatusPill>
            <span className="text-xs text-zinc-500">Generated {trust?.generated_at ? timeAgo(trust.generated_at) : "—"}</span>
          </div>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Commercialization gate: verifies scheduler freshness, active signal hygiene, market freshness, and backup recency before users see current opportunities.
          </p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {checks.map((c) => (
          <div key={c.id} className="rounded-md border border-zinc-800 bg-[#09090b] p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-zinc-200">{c.label}</div>
                <div className="mt-1 text-xs text-zinc-500">{c.detail}</div>
              </div>
              <StatusPill tone={trustTone(c.status)}>{c.status}</StatusPill>
            </div>
            {c.examples?.length > 0 && (
              <div className="mt-2 rounded border border-zinc-800 p-2 text-[11px] text-zinc-500 max-h-28 overflow-auto">
                {c.examples.map((e) => (
                  <div key={`${e.signal_id}-${e.reason}`}>{e.reason}: {e.title || e.market_id}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}


function ActivationMetricsCard({ metrics }) {
  const funnel = metrics?.funnel || [];
  return (
    <Card title="Activation analytics" testid="activation-analytics" className="border-zinc-700">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-zinc-400">
          First-party beta metrics for the last {metrics?.days || 7} days.
        </div>
        <div className="flex gap-2">
          <StatusPill tone="zinc">{fmtNum(metrics?.total_events || 0)} events</StatusPill>
          <StatusPill tone="zinc">{fmtNum(metrics?.active_users || 0)} users</StatusPill>
        </div>
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        {funnel.map((row) => (
          <div key={row.event} className="rounded-md border border-zinc-800 bg-[#09090b] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{(row.event || "event").replaceAll("_", " ")}</div>
            <div className="mt-1 font-mono-num text-lg text-zinc-100">{fmtNum(row.count)}</div>
            <div className="text-[11px] text-zinc-500">{fmtNum(row.users)} user{row.users === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}



function PlanMatrixCard({ plansData }) {
  const plans = plansData?.plans || {};
  return (
    <Card title="Plan gates" testid="plan-gates" className="border-zinc-700">
      <div className="text-sm text-zinc-400 mb-3">Internal paywall matrix. Stripe will update user plans later; these gates already decide access.</div>
      <div className="grid gap-2 md:grid-cols-4">
        {Object.entries(plans).map(([plan, cfg]) => (
          <div key={plan} className="rounded-md border border-zinc-800 bg-[#09090b] p-3">
            <div className="text-sm font-semibold text-zinc-100 uppercase">{plan}</div>
            <div className="mt-2 text-xs text-zinc-500">Watchlists: {cfg.watchlists}</div>
            <div className="text-xs text-zinc-500">Tracked items: {cfg.watchlist_items}</div>
            <div className="text-xs text-zinc-500">Alerts: {cfg.alerts}</div>
            <div className="text-xs text-zinc-500">History: {cfg.history_limit}</div>
            <div className="mt-2 text-[11px] text-zinc-600">Chat AI: {cfg.ai?.chat_assistant ?? 0}/day</div>
            <div className="text-[11px] text-zinc-600">Watchlist AI: {cfg.ai?.watchlist_copilot ?? 0}/day</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function UsersCard({ usersData, createUserMut, updateUserMut, resetPasswordMut, tempPassword }) {
  const [form, setForm] = useState({ email: "", name: "", plan: "beta", role: "user", account_status: "active" });
  const users = usersData?.users || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Card title="Beta users & access" testid="beta-users" className="border-zinc-700">
      <div className="grid gap-3 lg:grid-cols-[1fr_1.4fr]">
        <form
          className="rounded-md border border-zinc-800 bg-[#09090b] p-3 space-y-2"
          onSubmit={(e) => { e.preventDefault(); createUserMut.mutate(form); }}
        >
          <div className="text-xs text-zinc-400">Provision a beta user. The temporary password is shown once; send it manually through your chosen channel.</div>
          <input className="w-full rounded border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="input-new-user-email" />
          <input className="w-full rounded border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm" placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="input-new-user-name" />
          <div className="grid grid-cols-3 gap-2">
            <select className="rounded border border-zinc-800 bg-zinc-950 px-2 py-2 text-xs" value={form.plan} onChange={(e) => set("plan", e.target.value)}>
              <option value="free">Free</option><option value="beta">Beta</option><option value="pro">Pro</option><option value="power">Power</option>
            </select>
            <select className="rounded border border-zinc-800 bg-zinc-950 px-2 py-2 text-xs" value={form.role} onChange={(e) => set("role", e.target.value)}>
              <option value="user">User</option><option value="admin">Admin</option>
            </select>
            <select className="rounded border border-zinc-800 bg-zinc-950 px-2 py-2 text-xs" value={form.account_status} onChange={(e) => set("account_status", e.target.value)}>
              <option value="active">Active</option><option value="invited">Invited</option><option value="suspended">Suspended</option>
            </select>
          </div>
          <Button type="submit" disabled={createUserMut.isPending} className="h-8 text-xs bg-zinc-100 text-zinc-950 hover:bg-white" data-testid="btn-create-beta-user">
            {createUserMut.isPending ? "Creating…" : "Create beta user"}
          </Button>
          {tempPassword && (
            <div className="rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200" data-testid="temp-password-box">
              Temporary password for {tempPassword.email}: <span className="font-mono">{tempPassword.password}</span>
            </div>
          )}
          {createUserMut.error && <div className="text-xs text-rose-300">{formatApiError(createUserMut.error?.response?.data?.detail || createUserMut.error.message)}</div>}
        </form>
        <div className="max-h-80 overflow-auto rounded-md border border-zinc-800">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-zinc-950 text-zinc-500"><tr><th className="p-2">User</th><th>Plan</th><th>Status</th><th>Role</th><th>Last login</th><th></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-zinc-800">
                  <td className="p-2"><div className="text-zinc-200">{u.name}</div><div className="text-zinc-500">{u.email}</div></td>
                  <td><select className="bg-zinc-950 border border-zinc-800 rounded px-1 py-1" value={u.plan || "free"} onChange={(e) => updateUserMut.mutate({ id: u.id, plan: e.target.value })}><option value="free">free</option><option value="beta">beta</option><option value="pro">pro</option><option value="power">power</option></select></td>
                  <td><select className="bg-zinc-950 border border-zinc-800 rounded px-1 py-1" value={u.account_status || "active"} onChange={(e) => updateUserMut.mutate({ id: u.id, account_status: e.target.value })}><option value="active">active</option><option value="invited">invited</option><option value="suspended">suspended</option></select></td>
                  <td>{u.role}</td>
                  <td className="text-zinc-500">{u.last_login_at ? timeAgo(u.last_login_at) : "Never"}</td>
                  <td><button className="text-zinc-300 hover:text-white" onClick={() => resetPasswordMut.mutate(u.id)}>Reset</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}


function BillingStatusCard({ billingData }) {
  return (
    <Card title="Billing" testid="billing-status" className="border-zinc-700">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-zinc-400">
          Stripe billing foundation. Checkout and webhooks are wired but stay disabled until Stripe keys and price IDs are configured.
        </div>
        <StatusPill tone={billingData?.stripe_enabled ? "emerald" : "amber"}>
          Stripe {billingData?.stripe_enabled ? "configured" : "not configured"}
        </StatusPill>
      </div>
      <div className="mt-3 text-xs text-zinc-500">
        When enabled, users can upgrade to Pro or Power via Stripe Checkout. Webhooks update plan/subscription fields automatically.
      </div>
    </Card>
  );
}


function BetaMetricsCard({ betaData }) {
  if (!betaData) return null;
  return (
    <Card title="Beta program" testid="beta-metrics" className="border-zinc-700">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total users</div>
          <div className="text-lg font-bold text-zinc-100">{betaData.total_users}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Active 7d</div>
          <div className="text-lg font-bold text-zinc-100">{betaData.active_7d}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Active 30d</div>
          <div className="text-lg font-bold text-zinc-100">{betaData.active_30d}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Waitlist</div>
          <div className="text-lg font-bold text-zinc-100">{betaData.waitlist_count}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-zinc-500 mb-1">Sources</div>
          {Object.entries(betaData.source_breakdown || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-zinc-400">
              <span>{k}</span><span>{v}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="text-zinc-500 mb-1">Plans</div>
          {Object.entries(betaData.plan_breakdown || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-zinc-400">
              <span className="capitalize">{k}</span><span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function MutationButton({ mutation, children, testid, primary = false }) {
  return (
    <Button
      size="sm"
      data-testid={testid}
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className={`h-8 text-xs ${primary ? "bg-zinc-100 text-zinc-950 hover:bg-white" : "bg-zinc-800 hover:bg-zinc-700"}`}
    >
      {mutation.isPending ? "Running…" : children}
    </Button>
  );
}

function StaleSignalsCard({ signals }) {
  const items = signals?.items || [];
  if (!items.length) return null;
  return (
    <Card title={`Stale/unsafe signals (${items.length})`} testid="stale-signals" className="border-amber-700">
      <div className="space-y-2">
        {items.slice(0, 20).map((s) => (
          <div key={s.signal_id} className="flex items-start gap-2 rounded border border-zinc-800 p-2 text-xs">
            <span className="shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">{s.reason}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-zinc-200">{s.market_title || s.market_id}</div>
              <div className="text-zinc-500">{s.signal_type} · {s.confidence}% · {s.lifecycle_status}</div>
            </div>
          </div>
        ))}
        {items.length > 20 && <div className="text-xs text-zinc-500">+{items.length - 20} more</div>}
      </div>
    </Card>
  );
}

function TrustAuditCard({ audit }) {
  const items = audit?.items || [];
  if (!items.length) return null;
  return (
    <Card title="Trust audit log (7 days)" testid="trust-audit" className="border-zinc-700">
      <div className="space-y-1 max-h-80 overflow-auto">
        {items.map((entry) => (
          <div key={entry.id || entry.generated_at} className="flex items-center gap-2 rounded border border-zinc-800 px-2 py-1.5 text-xs">
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
              entry.overall_status === "pass" ? "bg-emerald-900/50 text-emerald-300" :
              entry.overall_status === "warn" ? "bg-amber-900/50 text-amber-300" :
              "bg-rose-900/50 text-rose-300"
            }`}>{entry.overall_status}</span>
            <span className="text-zinc-500 font-mono-num">{new Date(entry.generated_at).toLocaleString()}</span>
            <span className="text-zinc-400">{entry.checks?.length || 0} checks</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const [lastAction, setLastAction] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const { data: health } = useQuery({
    queryKey: ["admin-health"],
    queryFn: async () => (await api.get("/admin/data-health")).data,
    refetchInterval: 15_000,
  });

  const { data: jobs } = useQuery({
    queryKey: ["admin-jobs"],
    queryFn: async () => (await api.get("/admin/jobs", { params: { limit: 25 } })).data,
    refetchInterval: 10_000,
  });

  const { data: trust } = useQuery({
    queryKey: ["admin-trust-health"],
    queryFn: async () => (await api.get("/admin/trust-health")).data,
    refetchInterval: 60_000,
  });

  const { data: activation } = useQuery({
    queryKey: ["admin-activation"],
    queryFn: async () => (await api.get("/admin/analytics/activation", { params: { days: 7 } })).data,
    refetchInterval: 60_000,
  });

  const { data: usersData } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
    refetchInterval: 30_000,
  });

  const { data: plansData } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: async () => (await api.get("/admin/plans")).data,
  });

  const { data: billingData } = useQuery({
    queryKey: ["admin-billing"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  const { data: betaData } = useQuery({
    queryKey: ["admin-beta"],
    queryFn: async () => (await api.get("/beta/metrics")).data,
  });

  const counts = health?.collection_counts || {};
  const jobsHealth = health?.jobs || {};
  const coverage = health?.coverage || {};
  const scheduler = health?.scheduler || {};
  const bootstrapState = useMemo(() => {
    const markets = counts.markets || 0;
    const traders = counts.traders || 0;
    const signals = counts.signals_active || 0;
    if (markets === 0) return { tone: "rose", label: "Needs market sync", detail: "No market data is loaded yet." };
    if (traders === 0) return { tone: "amber", label: "Markets loaded, traders pending", detail: "Market data is present. Trader sync has not run yet." };
    if (signals === 0) return { tone: "amber", label: "Signals pending", detail: "Markets and traders exist. Scores/consensus/signals still need a cycle." };
    return { tone: "emerald", label: "Bootstrap complete", detail: "Markets, traders, and active signals are present." };
  }, [counts.markets, counts.traders, counts.signals_active]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    qc.invalidateQueries({ queryKey: ["admin-health"] });
  };

  const doMut = (label, endpoint) => async () => {
    setLastAction({ label, status: "running" });
    try {
      const res = await api.post(endpoint);
      setLastAction({ label, status: "success", data: res.data });
      return res.data;
    } catch (err) {
      setLastAction({ label, status: "error", error: formatApiError(err?.response?.data?.detail || err.message) });
      throw err;
    } finally {
      invalidate();
    }
  };

  const marketMut = useMutation({ mutationFn: doMut("Sync markets", "/admin/sync/markets") });
  const traderMut = useMutation({ mutationFn: doMut("Sync traders", "/admin/sync/traders") });
  const scoreMut = useMutation({ mutationFn: doMut("Recalc scores", "/admin/recalc/scores") });
  const consMut = useMutation({ mutationFn: doMut("Recalc consensus", "/admin/recalc/consensus") });
  const sigMut = useMutation({ mutationFn: doMut("Regenerate signals", "/admin/regenerate/signals") });
  const resolveMut = useMutation({ mutationFn: doMut("Resolve outcomes", "/admin/resolve/signals") });
  const repairSignalHygieneMut = useMutation({ mutationFn: doMut("Repair signal hygiene", "/admin/trust-health/repair-signal-hygiene") });

  const { data: staleSignals } = useQuery({
    queryKey: ["admin-stale-signals"],
    queryFn: async () => (await api.get("/admin/stale-signals?limit=50")).data,
    refetchInterval: 120_000,
  });

  const { data: trustAudit } = useQuery({
    queryKey: ["admin-trust-audit"],
    queryFn: async () => (await api.get("/admin/trust-audit?days=7&limit=20")).data,
    refetchInterval: 300_000,
  });
  const priceMut = useMutation({ mutationFn: doMut("Sample prices", "/admin/sample/prices") });
  const fullMut = useMutation({ mutationFn: doMut("Run full bootstrap cycle", "/admin/sync/full") });
  const createUserMut = useMutation({
    mutationFn: async (body) => (await api.post("/admin/users", body)).data,
    onSuccess: (data) => { setTempPassword({ email: data.user.email, password: data.temporary_password }); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });
  const updateUserMut = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/admin/users/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const resetPasswordMut = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/users/${id}/reset-password`)).data,
    onSuccess: (data) => { setTempPassword({ email: data.user.email, password: data.temporary_password }); qc.invalidateQueries({ queryKey: ["admin-users"] }); },
  });

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin</div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">Data bootstrap & health</h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500">
            Use this page to verify freshness, coverage, and scheduler activity without reducing what MarketTrace collects.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusPill tone="zinc">Preview mode</StatusPill>
          <StatusPill tone={scheduler.enabled ? "emerald" : "rose"}>Scheduler {scheduler.enabled ? "on" : "off"}</StatusPill>
          <StatusPill tone={scheduler.startup_ingestion ? "amber" : "zinc"}>Startup ingestion {scheduler.startup_ingestion ? "on" : "off"}</StatusPill>
          <StatusPill tone={bootstrapState.tone}>{bootstrapState.label}</StatusPill>
        </div>
      </div>

      <TrustHealthCard trust={trust} />

      <StaleSignalsCard signals={staleSignals} />

      <TrustAuditCard audit={trustAudit} />

      <ActivationMetricsCard metrics={activation} />

      <UsersCard usersData={usersData} createUserMut={createUserMut} updateUserMut={updateUserMut} resetPasswordMut={resetPasswordMut} tempPassword={tempPassword} />

      <PlanMatrixCard plansData={plansData} />

      <BillingStatusCard billingData={billingData} />

      <BetaMetricsCard betaData={betaData} />

      <Card title="Bootstrap status" testid="bootstrap-status" className="border-zinc-700">
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <div className="text-sm text-zinc-300">{bootstrapState.detail}</div>
            <ol className="mt-3 grid gap-2 text-xs text-zinc-500 md:grid-cols-3">
              <li className="rounded border border-zinc-800 p-3"><span className="text-zinc-300">1.</span> Sync markets</li>
              <li className="rounded border border-zinc-800 p-3"><span className="text-zinc-300">2.</span> Sync traders</li>
              <li className="rounded border border-zinc-800 p-3"><span className="text-zinc-300">3.</span> Run full cycle</li>
            </ol>
          </div>
          {lastAction ? (
            <div className="rounded-md border border-zinc-800 bg-[#09090b] p-3" data-testid="last-action-result">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Last action</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-zinc-200">
                {lastAction.label}
                <StatusPill tone={lastAction.status === "success" ? "emerald" : lastAction.status === "error" ? "rose" : "amber"}>{lastAction.status}</StatusPill>
              </div>
              <pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-[11px] text-zinc-500">
                {lastAction.error || JSON.stringify(lastAction.data || {}, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-zinc-800 p-3 text-sm text-zinc-500">
              Run a sync action to see the result here.
            </div>
          )}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Markets" value={counts.markets || 0} tone={(counts.markets || 0) > 0 ? "text-emerald-300" : "text-rose-300"} />
        <Metric label="Traders" value={counts.traders || 0} tone={(counts.traders || 0) > 0 ? "text-emerald-300" : "text-amber-300"} />
        <Metric label="Active signals" value={counts.signals_active || 0} tone={(counts.signals_active || 0) > 0 ? "text-emerald-300" : "text-amber-300"} />
        <Metric label="Raw records" value={counts.raw_records || 0} />
        <Metric label="Ref coverage %" value={coverage.referenced_coverage_pct ?? 0} tone={(coverage.referenced_coverage_pct || 0) >= 90 ? "text-emerald-300" : (coverage.referenced_coverage_pct || 0) >= 60 ? "text-amber-300" : "text-rose-300"} />
        <Metric label="Open-pos coverage %" value={coverage.open_position_coverage_pct ?? 0} tone={(coverage.open_position_coverage_pct || 0) >= 90 ? "text-emerald-300" : (coverage.open_position_coverage_pct || 0) >= 60 ? "text-amber-300" : "text-rose-300"} />
        {Object.entries(counts).filter(([k]) => !["markets", "traders", "signals_active", "raw_records"].includes(k)).map(([k, v]) => (
          <Metric key={k} label={k.replaceAll("_", " ")} value={v} />
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Ingestion controls" testid="ingestion-controls">
          <div className="flex flex-wrap gap-2">
            <MutationButton mutation={marketMut} testid="btn-sync-markets">Sync markets</MutationButton>
            <MutationButton mutation={traderMut} testid="btn-sync-traders">Sync traders</MutationButton>
            <MutationButton mutation={scoreMut} testid="btn-recalc-scores">Recalc scores</MutationButton>
            <MutationButton mutation={consMut} testid="btn-recalc-consensus">Recalc consensus</MutationButton>
            <MutationButton mutation={sigMut} testid="btn-regen-signals">Regenerate signals</MutationButton>
            <MutationButton mutation={resolveMut} testid="btn-resolve-signals">Resolve outcomes</MutationButton>
            <MutationButton mutation={repairSignalHygieneMut} testid="btn-repair-signal-hygiene">Repair signal hygiene</MutationButton>
            <MutationButton mutation={priceMut} testid="btn-sample-prices">Sample prices</MutationButton>
            <MutationButton mutation={fullMut} testid="btn-full-cycle" primary>Run full bootstrap cycle</MutationButton>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">
            Full cycle: markets → traders → positions → reconstruct → scores → consensus → signals. Use it only after markets are loading cleanly.
          </div>
        </Card>

        <Card title="Pipeline freshness" testid="last-syncs">
          <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] text-zinc-500">
            <div className="rounded border border-zinc-800 p-2">Market sync limit: <span className="text-zinc-300 font-mono-num">{scheduler.markets_sync_limit || "—"}</span></div>
            <div className="rounded border border-zinc-800 p-2">Referenced limit: <span className="text-zinc-300 font-mono-num">{scheduler.referenced_markets_sync_limit || "—"}</span></div>
            <div className="rounded border border-zinc-800 p-2">Tracked trader limit: <span className="text-zinc-300 font-mono-num">{scheduler.tracked_trader_limit || "—"}</span></div>
            <div className="rounded border border-zinc-800 p-2">Market scan: <span className="text-zinc-300 font-mono-num">{scheduler.trader_discovery_market_scan || "—"}</span></div>
          </div>
          <div className="mb-3 rounded border border-zinc-800 p-2 text-[11px] text-zinc-500">
            Referenced market coverage: <span className="text-zinc-300 font-mono-num">{coverage.known_referenced_markets || 0}/{coverage.referenced_markets || 0}</span> ·
            Open-position coverage: <span className="text-zinc-300 font-mono-num">{coverage.known_open_position_referenced_markets || 0}/{coverage.open_position_referenced_markets || 0}</span>
          </div>
          <div className="space-y-3 text-sm">
            {Object.entries(jobsHealth).map(([k, v]) => (
              <div key={k} className="rounded border border-zinc-800 p-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-zinc-300 text-xs">{k.replaceAll("_", " ")}</span>
                  <span className="text-zinc-400 text-[11px]">
                    {v.last_success ? timeAgo(v.last_success) : <span className="text-amber-300">Never</span>}
                  </span>
                </div>
                {v.last_metrics && (
                  <div className="mt-1 text-[11px] font-mono-num text-zinc-500">
                    {Object.entries(v.last_metrics).map(([mk, mv]) => `${mk}:${mv}`).join("  ")}
                  </div>
                )}
                {v.last_failure && <div className="mt-1 text-[11px] text-rose-300">Last failure: {timeAgo(v.last_failure)}</div>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-zinc-100 mb-2">Job history</h2>
        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-right px-3 py-2">Metrics</th>
              </tr>
            </thead>
            <tbody>
              {(jobs || []).map((j) => (
                <tr key={j.id} className="border-t border-zinc-800/70" data-testid={`job-${j.id}`}>
                  <td className="px-3 py-2">{j.job_type}</td>
                  <td className={`px-3 py-2 text-xs ${
                    j.status === "success" ? "text-emerald-400"
                      : j.status === "failed" ? "text-rose-400" : "text-amber-300"
                  }`}>{j.status}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{timeAgo(j.started_at)}</td>
                  <td className="px-3 py-2 text-right text-[11px] font-mono-num text-zinc-500">
                    {j.metrics ? Object.entries(j.metrics).map(([k, v]) => `${k}:${v}`).join(" ") : "—"}
                  </td>
                </tr>
              ))}
              {(!jobs || jobs.length === 0) && (
                <tr><td colSpan={4} className="text-xs text-zinc-500 text-center py-6">No jobs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
